import { Loader } from '../../Loader/Loader';
import React, { FC, Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureContext } from './FeatureContext';
import { FeatureUnavailable } from './FeatureUnavailable';

export interface FeatureGuardProps {
  /**
   * Name of the feature expected.
   *
   * @type {string}
   * @memberof FeatureGuardProps
   */
  name: string;
  children?: React.ReactNode;

  /**
   * Will be rendered if the expected feature does not match
   * and the property "showPlaceholder" is set to true.
   *
   * @type {ReactNode}
   * @memberof FeatureGuardProps
   */
  unavailablePlaceholder?: ReactNode;

  /**
   * If set, a placeholder will be rendered.
   * If no specific placeholder is given. A default placeholder will be rendered.
   *
   * @type {boolean}
   * @memberof FeatureGuardProps
   */
  showPlaceholder?: boolean;

  /**
   * If set, no content or loader will be shown while loading.
   *
   * @type {boolean}
   * @memberof FeatureGuardProps
   */
  hideWhileLoading?: boolean;
}

/**
 * Can be used to wrap components that should be rendered if the specified feature
 * is available and enabled.
 *
 * Must be used within a FeatureProvider.
 *
 * @example
 * <FeatureGuard name="Hybrid" showPlaceholder>
 *   <HybridSettings />
 * </FeatureGuard>
 */
export const FeatureGuard: FC<FeatureGuardProps> = ({
  name,
  children,
  unavailablePlaceholder,
  showPlaceholder,
  hideWhileLoading,
}) => {
  const { t } = useTranslation();
  const { hasFeature, isLoaded } = useFeatureContext();

  const matchesRequirement = hasFeature(name);

  const placeholder = (
    <div className="flex flex-1 items-center justify-center">
      {unavailablePlaceholder ?? <FeatureUnavailable name={t(name)} />}
    </div>
  );

  const loading = !hideWhileLoading && !isLoaded;
  const shouldShowPlaceholder = !hideWhileLoading || (hideWhileLoading && !isLoaded);

  return (
    <>
      {matchesRequirement ? (
        children
      ) : // eslint-disable-next-line unicorn/no-nested-ternary
      loading ? (
        <div className="flex flex-col items-center">
          <Loader />
          <Fragment>
            {t('feature_loading', { defaultValue: 'Feature wird geladen...' })}
          </Fragment>
        </div>
      ) : (
        shouldShowPlaceholder && showPlaceholder && placeholder
      )}
    </>
  );
};

FeatureGuard.displayName = 'FeatureGuard';
